import Fuse, { IFuseOptions } from 'fuse.js';
import { RefObject, useLayoutEffect, useRef } from 'react';
import { isV2 } from '../../../utils/widgetVersion';
import { FUZZY_MATCH_OPTIONS } from './search';

/** Highlight names per design version — each has its own `::highlight(...)` rule (`general.css` / `tokens.v2.css`). */
export const HIGHLIGHT_NAME_V1 = 'list-search';
export const HIGHLIGHT_NAME_V2 = 'list-search-v2';

/**
 * Single characters count as a matched run: a query like "spa" matches "Indonesia" on `s` + `a` alone,
 * and those letters are exactly what the row has to show for its place in the results.
 */
const MIN_FUZZY_MATCH_LENGTH = 1;

/** A matched character run, both ends inclusive — the shape fuse.js reports its indices in. */
type TMatchRange = readonly [number, number];

type TTextNodeEntry = { nodeIndex: number; text: string };

/** The hook only needs the titles of the rendered items — anything row-shaped fits. */
type TSearchHighlightItem = { title?: string };

const FUZZY_NODE_OPTIONS: IFuseOptions<TTextNodeEntry> = {
    ...FUZZY_MATCH_OPTIONS,
    keys: ['text'],
    includeMatches: true,
    minMatchCharLength: MIN_FUZZY_MATCH_LENGTH,
};

const normalize = (text?: string | null) => text?.trim().toLowerCase() ?? '';

const collectTitles = (items: readonly TSearchHighlightItem[]) => {
    const titles = new Set<string>();
    for (const { title } of items) {
        const normalized = normalize(title);
        if (normalized) {
            titles.add(normalized);
        }
    }
    return titles;
};

const findExactRanges = (text: string, needle: string): TMatchRange[] => {
    const ranges: TMatchRange[] = [];
    const haystack = text.toLowerCase();

    let from = haystack.indexOf(needle);
    while (from !== -1) {
        ranges.push([from, from + needle.length - 1]);
        from = haystack.indexOf(needle, from + needle.length);
    }
    return ranges;
};

const addRanges = (highlight: Highlight, node: Node, ranges: readonly TMatchRange[]) => {
    for (const [start, end] of ranges) {
        const range = new Range();
        range.setStart(node, start);
        range.setEnd(node, end + 1);
        highlight.add(range);
    }
};

/**
 * Highlights the search matches inside `containerRef` via the CSS Custom Highlight API,
 * so the rendered text stays untouched (no `<mark>` injection, no HTML parsing of item titles).
 * The paint style depends on the design version (`isV2`: `version` 2, or no version with the rebranding switch on):
 * v1 is `::highlight(list-search)` in `theme/general.css`, v2 is `::highlight(list-search-v2)` in `theme/tokens.v2.css`.
 * `::highlight()` is a global pseudo-element that a CSS module cannot scope, so the version picks the name instead.
 *
 * Two passes per text node, mirroring how the list itself matches (see `filterBySearch`):
 * every exact occurrence of the query is painted, and nodes without one fall back to the same
 * fuzzy engine — so a partial match ("Russya", "Franc") still gets its matched runs painted.
 *
 * Only **item titles** are painted, because that is what the list matches on (`title`/`alias`).
 * The rows also render subtitles, meta and amounts, and painting those would show matches the
 * inclusion logic never considered — e.g. an address row `title: "123 Main Street"` with
 * `subtitle: "London, UK"` must stay unpainted for the query "london". Titles are recognised by
 * comparing a text node against the titles of `items`, so no row component needs to mark up its
 * title element: a title rendered as something other than `item.title` simply stays unpainted
 * instead of dragging unrelated text in.
 *
 * Hence `items` must be the list currently rendered inside the container — it both scopes the paint
 * and re-runs the effect once the DOM has been updated with new rows.
 *
 * Single-instance assumption: the highlight is registered under one shared name per version (`list-search` / `list-search-v2`),
 * and the `CSS.highlights` registry is keyed by name — so only ONE searchable list may be mounted at
 * a time. Two mounted simultaneously would overwrite each other's highlights. This matches product
 * usage (search opens in a modal, one at a time). To support several at once, switch to a per-instance
 * name plus a dynamically injected `::highlight(...)` rule.
 */
export const useSearchHighlight = (
    containerRef: RefObject<HTMLElement | null>,
    query: string,
    items: readonly TSearchHighlightItem[],
    version: number | null | undefined,
) => {
    const highlightRef = useRef<Highlight | null>(null);
    const name = isV2({ version }) ? HIGHLIGHT_NAME_V2 : HIGHLIGHT_NAME_V1;

    useLayoutEffect(() => {
        if (typeof CSS === 'undefined' || !CSS.highlights) {
            return;
        }
        if (!highlightRef.current) {
            highlightRef.current = new Highlight();
        }
        const highlight = highlightRef.current;
        highlight.clear();

        const container = containerRef.current;
        const needle = query.trim().toLowerCase();

        const titles = collectTitles(items);

        if (container && needle && titles.size > 0) {
            const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
                acceptNode: (node) => (titles.has(normalize(node.textContent)) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
            });
            const nodes: Node[] = [];
            const fuzzyCandidates: TTextNodeEntry[] = [];

            for (let node = walker.nextNode(); node; node = walker.nextNode()) {
                const text = node.textContent ?? '';
                const nodeIndex = nodes.push(node) - 1;
                const exactRanges = findExactRanges(text, needle);

                if (exactRanges.length > 0) {
                    addRanges(highlight, node, exactRanges);
                } else {
                    fuzzyCandidates.push({ nodeIndex, text });
                }
            }

            if (fuzzyCandidates.length > 0) {
                const fuse = new Fuse(fuzzyCandidates, FUZZY_NODE_OPTIONS);
                for (const { item, matches } of fuse.search(needle)) {
                    const indices = matches?.find((match) => match.key === 'text')?.indices;
                    if (indices?.length) {
                        addRanges(highlight, nodes[item.nodeIndex], indices);
                    }
                }
            }
        }
        if (highlight.size > 0) {
            CSS.highlights.set(name, highlight);
        } else {
            CSS.highlights.delete(name);
        }

        return () => {
            highlight.clear();
            CSS.highlights?.delete(name);
        };
    }, [containerRef, query, items, name]);
};
